import React, { useState } from 'react';
import styled from 'styled-components';
import axios, { AxiosResponse } from 'axios';
import swal from 'sweetalert2';
import { Formik, Form, FormikConfig } from 'formik';
import {
  ParsedUser,
  LockboxWithMlsNumber,
  ESLockbox,
} from '@bb/domain/dist/types';
import { LockboxVendorSettings } from '@bb/automaton';

import { Button, theme } from 'components';
import { featureEnabled } from 'utils';
import { Color } from '@bb/elements-web';
import BrandSelectRow from './BrandSelectRow';
import LockboxAgentSelectRow from './LockboxAgentSelectRow';
import SharingCodeInputRow from './SharingCodeInputRow';
import SerialNumberInputRow from './SerialNumberInputRow';
import SerialNumberSelectRow from './SerialNumberSelectRow';

type AddNewLockboxProps = {
  auth: ParsedUser & { isAdmin?: boolean; roles: any } & {
    org: { lockboxVendorSettings?: LockboxVendorSettings };
  };
  lockbox: ESLockbox | null | undefined;
  setShowTable: (value: boolean) => void;
  onLockboxAdded: () => void;
};

const AddNewLockbox: React.FC<AddNewLockboxProps> = ({
  auth,
  lockbox,
  setShowTable,
  onLockboxAdded,
}) => {
  const [lockboxLength, setLockboxLength] = useState(0);
  const hasMyBrokerageFeature = auth.org.features.includes('MY_BROKERAGE');
  const isAdmin = auth.roles[auth.role] >= auth.roles.TEAM_ADMIN;

  const showTable = () => {
    setShowTable(true);
  };

  const getInitialUser = () => {
    if (!hasMyBrokerageFeature) {
      return auth.id;
    }
    return lockbox?.assignee?.id || (isAdmin ? '' : auth.id);
  };

  // Formik
  const initialValues = {
    brand: lockbox?.brand || '',
    serialNumber: lockbox?.serialNumber || '',
    lockboxList: [] as LockboxWithMlsNumber[],
    sharingCode: lockbox?.sharingCode || '',
    user: getInitialUser(),
  };

  const validate: FormikConfig<typeof initialValues>['validate'] = values => {
    const errors: Record<string, string> = {};

    if (!values.brand) {
      errors.brand = 'Lockbox brand is required';
    }
    if (values.brand !== 'sentriLock' && !values.serialNumber) {
      errors.serialNumber = 'Serial number is required';
    }
    // validate serial number if not valid throw error here as well
    if (values.brand === 'masterLock' && !values.sharingCode) {
      errors.sharingCode = 'Sharing code is required';
    }
    if (isAdmin && !values.user) {
      errors.user = 'An agent is required';
    }

    return errors;
  };

  const editOrAddSwal = <T extends { _id?: string }>(
    values: T,
    brand?: string,
  ) => {
    if (brand === 'sentriLock') {
      swal.fire({
        title: 'Lockbox Synced',
        text: 'Lockbox has been successfully synced.',
        type: 'success',
      });
    } else {
      swal.fire({
        title: `Lockbox ${values._id ? 'edited' : 'added'}`,
        text: `Lockbox has been successfully ${
          values._id ? 'edited' : 'added'
        }.`,
        type: 'success',
      });
    }
    onLockboxAdded();
  };

  const errorSwal = (err: AxiosResponse) => {
    if (err.status === 400) {
      return swal.fire({
        title: 'Invalid Lockbox',
        text: 'Please confirm that the sharing code and device id are correct.',
        type: 'error',
      });
    }
    return swal.fire({
      title: 'Lockbox already registered',
      text: `${err.data.message || 'Error registering Lockbox.'}`,
      type: 'error',
    });
  };

  const handleOnSubmit: FormikConfig<typeof initialValues>['onSubmit'] = async (
    values,
    { setSubmitting },
  ) => {
    const data = {
      _id: lockbox?._id,
      ...values,
      serialNumber: values.serialNumber.toUpperCase(),
    };

    setSubmitting(true);

    await axios
      .post('/lockbox', { lockbox: data })
      .then(() => editOrAddSwal(data, values.brand))
      .catch(err => errorSwal(err.response))
      .finally(() => setSubmitting(false));
  };

  return (
    <StyledContainer>
      <StyledHeader>Lockbox Details</StyledHeader>
      <hr />
      <Formik
        initialValues={initialValues}
        validate={values => validate(values)}
        onSubmit={handleOnSubmit}
      >
        {({
          values,
          isSubmitting,
          handleChange,
          setValues,
          setFieldTouched,
          errors,
        }) => (
          <StyledForm>
            <StyledTable>
              <StyledTableBody>
                <BrandSelectRow
                  inputName={'brand'}
                  setFieldTouched={setFieldTouched}
                  resetFieldValues={(value: 'masterLock' | 'sentriLock') => {
                    setLockboxLength(0);
                    setValues({
                      brand: value,
                      serialNumber: '',
                      lockboxList: [],
                      sharingCode: '',
                      user: getInitialUser(),
                    });
                  }}
                  lockboxVendorSettings={auth.org.lockboxVendorSettings}
                />
                {hasMyBrokerageFeature && isAdmin && (
                  <LockboxAgentSelectRow
                    selectedAgentId={values.user}
                    handleChange={value => {
                      setLockboxLength(0);
                      handleChange('user')(value);
                    }}
                    inputName={'user'}
                    disabled={values.brand === ''}
                    setFieldTouched={setFieldTouched}
                  />
                )}

                {values.brand === 'masterLock' && (
                  <>
                    <SerialNumberInputRow inputName={'serialNumber'} />
                    <SharingCodeInputRow inputName={'sharingCode'} />
                  </>
                )}
                {values.brand === 'sentriLock' && !!values.user && (
                  <SerialNumberSelectRow
                    userId={values.user}
                    inputName={'lockboxList'}
                    handleChange={value => {
                      handleChange('lockboxList')(value);
                    }}
                    selectedSerialNumbers={values.lockboxList}
                    setLockboxLength={setLockboxLength}
                  />
                )}
              </StyledTableBody>
            </StyledTable>

            <div className="pull-right">
              <StyledConfirmButton
                disabled={
                  isSubmitting ||
                  (values.brand === 'sentriLock' && !lockboxLength) ||
                  !values?.brand ||
                  Object.keys(errors)?.length > 0
                }
                htmlType="submit"
                className={`btn btn-${
                  values.brand === 'sentriLock' ? 'info' : 'primary'
                }`}
                $hasSupraRebrandingFeature={featureEnabled(
                  'supraRebrandingFeature',
                  1,
                )}
              >
                {values.brand === 'sentriLock' ? 'Sync Lockboxes' : 'Save'}
              </StyledConfirmButton>
              <StyledCancelButton
                className="btn btn-default"
                onClick={showTable}
              >
                Cancel
              </StyledCancelButton>
            </div>
          </StyledForm>
        )}
      </Formik>
    </StyledContainer>
  );
};

const StyledHeader = styled.h3`
  font-size: 24px;
  font-weight: 200;
  margin: 10 0 10 0;
`;

const StyledContainer = styled.div`
  margin: 17px;
`;

const StyledForm = styled(Form)`
  && {
    max-width: 50%;
  }
`;

const StyledTable = styled.table`
  min-width: 280px;
  margin: 15px;

  && {
    border-collapse: separate;
    border-spacing: 10px;
  }

  && .ant-checkbox-wrapper {
    border-spacing: initial;
  }

  && label.error,
  label span.error {
    white-space: pre;
    color: #e74c3c;
    margin: 5px 0 0 0;
    font-weight: 400;
  }

  && label span.error {
    margin-left: 5px;
  }

  && label.error:before {
    content: '\f071';
    font-family: 'FontAwesome';
    margin: 0 4px;
  }
`;

const StyledTableBody = styled.tbody`
  width: 100%;
`;

const StyledButton = styled(Button)`
  margin: 2px;
  line-height: 10px;
  font-weight: 600;
  border-radius: 3px;
  padding: 8px;
`;

const StyledConfirmButton = styled(StyledButton)<{
  $hasSupraRebrandingFeature?: boolean;
}>`
  background-color: ${({ $hasSupraRebrandingFeature }) =>
    $hasSupraRebrandingFeature ? Color.PRIMARY : theme.blue6};
  color: #fff;
`;

const StyledCancelButton = styled(StyledButton)`
  background-color: #fff;
  border: 1px solid #e4e5e7;
  color: #6a6c6f;
`;

export default AddNewLockbox;
